/** Tool definitions: Tax estimates, realized gains and tax income. */

import { ExternalIncomeType } from '../../enums/IncomeType.js';
import type { ToolDefinition } from './types.js';

export const taxTools = {
  calculate_tax_estimate: {
    name: 'calculate_tax_estimate',
    access: 'read',
    description:
      'Calculate comprehensive tax estimate for a given year including federal and state taxes, quarterly payment schedule, and tax liability breakdown. Analyzes realized gains, dividends, interest income, and W-2 income. Returns detailed quarterly estimates and tax planning information.',
    inputSchema: {
      type: 'object',
      properties: {
        taxYear: {
          type: 'number',
          description: 'Tax year to calculate (e.g., 2024, 2025)',
        },
        portfolioId: {
          type: 'string',
          description: 'Optional portfolio ID to calculate taxes for. If omitted, uses all portfolios.',
        },
        filingStatus: {
          type: 'string',
          enum: ['single', 'married_filing_jointly', 'married_filing_separately', 'head_of_household'],
          description: 'Tax filing status. Defaults to single if not specified.',
        },
        state: {
          type: 'string',
          description: 'Two-letter state code (e.g., "CA", "NY"). If omitted, only calculates federal taxes.',
        },
        projectFullYear: {
          type: 'boolean',
          description:
            'When true, projects full year income/tax for current year instead of YTD actuals. Useful for planning.',
        },
        includeOpenOptions: {
          type: 'boolean',
          description: 'When true, includes open options premiums as potential short-term gains for tax planning.',
        },
      },
      required: ['taxYear'],
    },
  },
  get_realized_gains: {
    name: 'get_realized_gains',
    access: 'read',
    description:
      'Get realized capital gains and losses for a tax year from the stored lot assignments — the same data as the Gains page and the tax estimate (per-sale lot methods, wash-sale disallowances and corporate actions applied). Returns each sale with its per-lot term split (taxProjection), short/long totals of raw, disallowed and recognized gain, and each account\'s taxability.',
    inputSchema: {
      type: 'object',
      properties: {
        taxYear: {
          type: 'number',
          description: 'Tax year to fetch realized gains for (e.g., 2024, 2025)',
        },
        portfolioId: {
          type: 'string',
          description: 'Optional portfolio ID to filter gains. If omitted, returns gains from all portfolios.',
        },
        accountIds: {
          type: 'array',
          items: { type: 'string' },
          description: 'Optional array of account IDs to filter gains.',
        },
        onlyTaxable: {
          type: 'boolean',
          description: 'Only include taxable accounts (skip IRAs and other tax-advantaged accounts).',
        },
      },
      required: ['taxYear'],
    },
  },
  get_quarterly_estimates: {
    name: 'get_quarterly_estimates',
    access: 'read',
    description:
      'Get quarterly tax payment schedule with recommended payment amounts for estimated taxes. Shows Q1-Q4 payments, due dates, and how much to pay each quarter based on current year income and gains. Essential for tax planning and avoiding underpayment penalties.',
    inputSchema: {
      type: 'object',
      properties: {
        taxYear: {
          type: 'number',
          description: 'Tax year for quarterly estimates (e.g., 2024, 2025)',
        },
        portfolioId: {
          type: 'string',
          description: 'Optional portfolio ID to calculate taxes for. If omitted, uses all portfolios.',
        },
        filingStatus: {
          type: 'string',
          enum: ['single', 'married_filing_jointly', 'married_filing_separately', 'head_of_household'],
          description: 'Tax filing status. Defaults to single if not specified.',
        },
        state: {
          type: 'string',
          description: 'Two-letter state code (e.g., "CA", "NY") for state tax estimates.',
        },
      },
      required: ['taxYear'],
    },
  },
  get_external_income_sources: {
    name: 'get_external_income_sources',
    access: 'read',
    description:
      'Get all external income sources (W-2, 1099-NEC, 1099-MISC, rental income, K-1, etc.) for a tax year. Shows income type, gross amount, withholding, recurring pay schedules, and tax estimates. Use this to answer questions about salary, contractor income, or non-investment income.',
    inputSchema: {
      type: 'object',
      properties: {
        taxYear: {
          type: 'number',
          description: 'Tax year to fetch external income for (e.g., 2024, 2025)',
        },
        type: {
          type: 'string',
          enum: Object.values(ExternalIncomeType),
          description: 'Optional filter by income type. If omitted, returns all types.',
        },
      },
      required: ['taxYear'],
    },
  },
  get_w2_income: {
    name: 'get_w2_income',
    access: 'read',
    description:
      'Get W-2 wage and salary income for a tax year, including recurring salaries that started in previous years but continue into the requested year. Shows employer info, gross wages, withholding amounts, recurring pay schedules (biweekly, monthly, etc.), annual salary, and estimated annual income. Automatically includes ongoing recurring income sources. Use this to answer questions about salary, paychecks, projected income, or employment income.',
    inputSchema: {
      type: 'object',
      properties: {
        taxYear: {
          type: 'number',
          description: 'Tax year to fetch W-2 income for (e.g., 2024, 2025, 2026). Includes both one-time payments in this year and recurring salaries that overlap this year.',
        },
      },
      required: ['taxYear'],
    },
  },
  get_1099_income: {
    name: 'get_1099_income',
    access: 'read',
    description:
      'Get 1099 income (1099-NEC, 1099-MISC) for a tax year. Shows contractor payments, non-employee compensation, and other 1099 income. Useful for freelancers, contractors, or self-employed individuals.',
    inputSchema: {
      type: 'object',
      properties: {
        taxYear: {
          type: 'number',
          description: 'Tax year to fetch 1099 income for (e.g., 2024, 2025)',
        },
      },
      required: ['taxYear'],
    },
  },
  get_tax_loss_harvest_candidates: {
    name: 'get_tax_loss_harvest_candidates',
    access: 'read',
    description:
      'Tax-loss harvest PLAN for the current tax year, lot by lot, in taxable accounts only (retirement-account losses are not deductible). ' +
      'Returns: realizedToDate (recognized short/long-term gains so far and the carryforward); every open lot with an unrealized loss (term, days until long-term, status eligible / washSaleBlocked with harvestableAfter date and reason); excludedPositions (positions left out and why: unknown basis, unreconciled history, missing from the last sync, no live quote, bonds); ' +
      'plan.sales — exactly which lots and how many shares to sell in the next market session (asOf) to reach the target, with proceeds and realized loss (short-term lots first, most loss per dollar sold first, last lot partial); ' +
      'before/after/savings from the same full tax estimate as the Taxes page: total, federal, state and NIIT tax, taxable short-term gain, the next estimated-payment installment (due date and amount due) and the balance due; ' +
      'symbolWarnings (recent purchases, put assignments or calls bought in ANY account incl. IRAs, open short puts, dividend reinvestment) and assumptions (specific-lot identification at the broker, no repurchase for 30 days, prior-year safe harbor effect). ' +
      'Read-only estimate: places no trades.',
    inputSchema: {
      type: 'object',
      properties: {
        portfolioId: {
          type: 'string',
          description: 'Portfolio ID to analyze.',
        },
        target: {
          type: 'string',
          enum: ['offsetShortTerm', 'offsetAllGains', 'maxDeduction'],
          description:
            "What to offset. 'offsetShortTerm' (default): bring the taxable short-term gain to zero. 'offsetAllGains': bring all taxable capital gain to zero. 'maxDeduction': all gains plus the $3,000 ($1,500 MFS) ordinary-income loss deduction.",
        },
        minLoss: {
          type: 'number',
          description: 'Skip lots whose unrealized loss is smaller than this many dollars. Defaults to 0.',
        },
        projectFullYear: {
          type: 'boolean',
          description: 'Project recurring income to year-end (same toggle as the Taxes page). Defaults to false (year to date).',
        },
        includeOpenOptions: {
          type: 'boolean',
          description: 'Count open option premiums as short-term gains (same toggle as the Taxes page). Defaults to false.',
        },
      },
      required: ['portfolioId'],
    },
  },
} satisfies Record<string, ToolDefinition>;
